import React from "react";

/**
 * Audio Comics Settings — rendered inside Admin → Settings.
 * Fully data-driven: FREE/PAID toggle, pricing card, launch offer banner
 * and CTA labels are all persisted to `platform_settings` via /api/settings
 * and drive the /audio-comics UI in real time (no code change needed).
 */
export default function AdminAudioComicsSettings({ settings, setSettings }) {
  const s = settings;
  const on = (k, v) => setSettings((prev) => ({ ...prev, [k]: v }));
  const mode = s.audio_mode || "FREE";

  return (
    <div className="bb-card p-6 mt-6" data-testid="admin-audio-comics-settings">
      <div className="bb-overline text-[var(--bb-muted)] mb-1">AUDIO COMICS</div>
      <h3 className="font-serif text-xl text-[var(--bb-forest)] mb-1">Audio Comics Settings</h3>
      <p className="text-xs text-[var(--bb-muted)] max-w-2xl">
        Toggle Audio Comics between <b>FREE</b> browsing / listening and <b>PAID</b> subscription mode.
        All labels, pricing and banner text below drive the live <code>/audio-comics</code> UI immediately after save.
      </p>

      {/* Access mode toggle */}
      <div className="mt-6">
        <label className="bb-overline text-[var(--bb-muted)]">Access mode</label>
        <div className="mt-2 inline-flex rounded-full border border-[var(--bb-border)] overflow-hidden">
          {["FREE", "PAID"].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => on("audio_mode", m)}
              className={`px-5 py-2 text-sm font-medium transition ${mode === m ? "bg-[var(--bb-forest)] text-white" : "text-[var(--bb-ink)]/70 hover:bg-[var(--bb-sand)]"}`}
              data-testid={`audio-mode-${m.toLowerCase()}`}
            >
              {m}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-[var(--bb-muted)] mt-2 max-w-xl">
          {mode === "FREE"
            ? "FREE mode: anyone can browse and listen without login. Payment flow is hidden."
            : "PAID mode: browsing stays free; playback requires Google Sign In + active Razorpay subscription."}
        </p>
      </div>

      {/* Subscription pricing */}
      <div className="mt-8 grid md:grid-cols-2 gap-5">
        <div>
          <label className="bb-overline text-[var(--bb-muted)]">Monthly price</label>
          <input
            type="number" min="0"
            value={s.subscription_price ?? 50}
            onChange={(e) => on("subscription_price", Number(e.target.value))}
            className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)]"
            data-testid="ac-setting-price"
          />
        </div>
        <div>
          <label className="bb-overline text-[var(--bb-muted)]">Currency</label>
          <input
            type="text"
            value={s.currency ?? "INR"}
            onChange={(e) => on("currency", e.target.value)}
            className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)]"
            data-testid="ac-setting-currency"
          />
        </div>
        <div>
          <label className="bb-overline text-[var(--bb-muted)]">Plan name</label>
          <input
            type="text"
            value={s.plan_name ?? "Single Plan"}
            onChange={(e) => on("plan_name", e.target.value)}
            className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)]"
            data-testid="ac-setting-plan-name"
          />
        </div>
        <div>
          <label className="bb-overline text-[var(--bb-muted)]">Plan description</label>
          <input
            type="text"
            value={s.plan_description ?? ""}
            onChange={(e) => on("plan_description", e.target.value)}
            className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)]"
            data-testid="ac-setting-plan-description"
          />
        </div>
      </div>

      {/* Launch banner */}
      <div className="mt-8">
        <label className="flex items-center justify-between gap-4">
          <div>
            <span className="bb-overline text-[var(--bb-muted)]">Launch offer banner</span>
            <div className="text-xs text-[var(--bb-muted)] max-w-md mt-1">Shown on the /audio-comics landing page when FREE mode is on.</div>
          </div>
          <input
            type="checkbox"
            checked={!!s.launch_offer_enabled}
            onChange={(e) => on("launch_offer_enabled", e.target.checked)}
            className="scale-125 accent-[var(--bb-terracotta)]"
            data-testid="ac-setting-launch-toggle"
          />
        </label>
        <input
          type="text"
          value={s.launch_offer_text ?? "FREE FOR A LIMITED PERIOD"}
          onChange={(e) => on("launch_offer_text", e.target.value)}
          disabled={!s.launch_offer_enabled}
          className="mt-3 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] disabled:opacity-40"
          data-testid="ac-setting-launch-text"
        />
      </div>

      {/* CTA buttons */}
      <div className="mt-8 grid md:grid-cols-2 gap-5">
        <div>
          <label className="bb-overline text-[var(--bb-muted)]">Primary CTA</label>
          <input
            type="text"
            value={s.cta_primary ?? "Browse Audio Library"}
            onChange={(e) => on("cta_primary", e.target.value)}
            className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)]"
            data-testid="ac-setting-cta-primary"
          />
        </div>
        <div>
          <label className="bb-overline text-[var(--bb-muted)]">Secondary CTA</label>
          <input
            type="text"
            value={s.cta_secondary ?? "Subscribe to Listen"}
            onChange={(e) => on("cta_secondary", e.target.value)}
            className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)]"
            data-testid="ac-setting-cta-secondary"
          />
        </div>
      </div>

      <div className="mt-6 rounded-md bg-[var(--bb-sand)]/60 border border-[var(--bb-border)] p-4 text-xs text-[var(--bb-ink)]/70 leading-relaxed">
        Changes go live on <code>/audio-comics</code> immediately after clicking <b>Save</b>. No redeploy or code change required.
      </div>
    </div>
  );
}
